import { Box, Button, Dialog, DialogTitle, Divider, OutlinedInput, Typography } from '@mui/material';
import {
  closeBtn,
  dialogContent,
  dialogContentBoxes,
  dialogTitle,
  paperProps,
} from 'app/components/RequisiteModal/RequisiteModal.sx';
import CloseIcon from 'app/components/Icons/CloseIcon';
import DialogContent from '@mui/material/DialogContent';
import CustomInput from 'app/components/CustomInput';
import SearchIcon from 'app/components/Icons/SearchIcon';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import logger from 'app/utils/logger';
import { debounce } from 'lodash';

const DEBOUNCE_TIME_MS = 300;

interface Props {
  getCodeData?: any;
  transferData?: any;
  dialogOpen?: any;
  dialogSet?: any;
  codeData?: any;
  clickCode?: any;
  handleSearchCode?: any;
  setIsSearch?: any;
  budget?: any;
  swiftCode?: any;
  country?: any;
  selectedAccount?: any;
  swiftCodeMediator?: any;
}

const BankDataModal = (props: Props) => {
  const {
    getCodeData,
    transferData,
    codeData,
    dialogSet,
    dialogOpen,
    clickCode,
    setIsSearch,
    budget,
    swiftCode,
    country,
    selectedAccount,
    swiftCodeMediator,
  } = props;

  const [searchState, setSearchState] = useState('');

  const descriptionElementRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (dialogOpen) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        let offset = 0;
        let value = 1;
        const handleScroll = (e: any) => {
          // Do your stuff here
          const { offsetHeight, scrollTop, scrollHeight } = e.target;

          if (Math.ceil(+offsetHeight + +scrollTop) + 1 >= scrollHeight) {
            setIsSearch(false);
            value = value + 1;
            offset = offset + 30;
            if (budget) {
              getCodeData({
                businessKey: transferData?.business_key,
                data: {
                  search: searchState,
                  offset,
                  popup: 'true',
                },
              });
            } else if (swiftCode) {
              getCodeData({
                businessKey: transferData?.business_key,
                data: {
                  search: searchState,
                  offset,
                  name: `bank_bic`,
                  bic_code: country.bicCode,
                  currency: selectedAccount.currency,
                },
              });
            } else if (swiftCodeMediator) {
              getCodeData({
                businessKey: transferData?.business_key,
                data: {
                  search: searchState,
                  offset,
                  name: `bank_bic`,
                },
              });
            } else {
              getCodeData({
                businessKey: transferData?.business_key,
                data: {
                  search: searchState,
                  action: 'bank_code',
                  offset,
                },
              });
            }
          }
        };

        logger.log(descriptionElementRef.current);

        descriptionElementRef?.current?.addEventListener('scroll', handleScroll);

        return () => descriptionElementRef?.current?.removeEventListener('scroll', handleScroll);
      }
    }
  }, [dialogOpen, searchState]);

  const handleSearchLocalTransferBankCode = useMemo(
    () =>
      debounce((e: React.ChangeEvent<HTMLInputElement>) => {
        setIsSearch(true);

        setSearchState(e.target.value);
        if (budget) {
          getCodeData({
            businessKey: transferData?.business_key,
            data: {
              search: e.target.value,
              offset: 0,
              popup: 'true',
            },
          });
        } else if (swiftCode) {
          getCodeData({
            businessKey: transferData?.business_key,
            data: {
              search: e.target.value,
              offset: 0,
              name: `bank_bic`,
              bic_code: country.bicCode,
              currency: selectedAccount.currency,
            },
          });
        } else if (swiftCodeMediator) {
          getCodeData({
            businessKey: transferData?.business_key,
            data: {
              search: e.target.value,
              offset: 0,
              name: `bank_bic`,
            },
          });
        } else {
          getCodeData({
            businessKey: transferData?.business_key,
            data: {
              search: e.target.value,
              action: 'bank_code',
              offset: 0,
            },
          });
        }
      }, DEBOUNCE_TIME_MS),
    []
  );

  useEffect(() => {
    return () => {
      handleSearchLocalTransferBankCode.cancel();
    };
  }, [handleSearchLocalTransferBankCode]);

  return (
    <Dialog
      open={dialogOpen}
      PaperProps={{
        sx: { borderRadius: '8px', width: '600px', maxWidth: '600px', maxHeight: 'none', height: '90vh' },
      }}
      onClose={() => dialogSet(false)}
      disablePortal
    >
      <DialogTitle sx={dialogTitle}>
        {budget ? (
          <Typography sx={{ fontSize: '26px', fontWeight: 600 }}>Təsnifatın kodu və adı</Typography>
        ) : swiftCode ? (
          <Typography sx={{ fontSize: '26px', fontWeight: 600 }}>Bankın Swift/BIC kodu və adı</Typography>
        ) : (
          <Typography sx={{ fontSize: '26px', fontWeight: 600 }}>Benefsiar bankın kodu və adı</Typography>
        )}
        <Button
          color={'tertiary'}
          onClick={() => {
            dialogSet(false);
          }}
          sx={closeBtn}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>
      <DialogContent sx={{ ...dialogContent, overflow: 'hidden' }}>
        <Box sx={dialogContentBoxes}>
          <OutlinedInput
            placeholder="Axtar"
            startAdornment={<SearchIcon />}
            sx={{ background: 'rgba(20, 25, 31, 0.06)' }}
            onInput={(e: any) => handleSearchLocalTransferBankCode(e)}
          />
        </Box>
        <Box sx={{ ...dialogContentBoxes, overflow: 'auto' }} id={'requisiteData'}>
          <Box sx={{ display: 'flex', gap: '25px', mt: '28px', mb: '16px' }}>
            <Typography sx={{ fontSize: '14px', color: 'rgba(20, 25, 31, 0.4)', minWidth: '140px', maxWidth: '140px' }}>
              Kodu
            </Typography>
            <Typography sx={{ fontSize: '14px', color: 'rgba(20, 25, 31, 0.4)' }}>Adı</Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              overflow: 'auto',
            }}
            ref={descriptionElementRef}
          >
            {codeData?.map((item: any) => (
              <Box key={item.branch_code ? item.branch_code : item.bic ? item.bic : item.code}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: '25px',
                    p: '16px 0',
                    alignItems: 'center',
                    '&:hover': { cursor: 'pointer' },
                  }}
                  onClick={() => {
                    if (item.branch_code) {
                      clickCode(item.branch_name, item.corr_acc, item.branch_code);
                    } else if (item.bic) {
                      clickCode(item.name, item.bic);
                    } else {
                      clickCode(item.classification, item.code);
                    }
                    dialogSet(false);
                  }}
                >
                  <Typography sx={{ fontSize: '17px', fontWeight: '600', minWidth: '140px', maxWidth: '140px' }}>
                    {item.branch_code ? item.branch_code : item.bic ? item.bic : item.code}
                  </Typography>
                  <Typography sx={{ fontSize: '17px', fontWeight: '600' }}>
                    {item.branch_name ? item.branch_name : item.bic ? item.name : item.classification}
                  </Typography>
                </Box>
                <Divider />
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BankDataModal;
